// Label + species mutators — pure (state, args) => patch.

import { speciesLabelKeys } from '../labels';
import { speciesLayer } from '../../plants';

function patchElement(state, id, fn) {
  let hit = false;
  const elements = state.elements.map((el) => {
    if (el.id !== id) return el;
    const next = fn(el);
    if (!next) return el;
    hit = true;
    return next;
  });
  return hit ? { elements } : null;
}

export function updateLabel(state, { elementId, index, patch }) {
  return patchElement(state, elementId, (el) => {
    const labels = el.data?.labels;
    if (!labels || !labels[index]) return null;
    return { ...el, data: { ...el.data, labels: labels.map((l, i) => (i === index ? { ...l, ...patch } : l)) } };
  });
}

export function toggleLabelKey(state, { elementId, index, key, on }) {
  return patchElement(state, elementId, (el) => {
    const labels = el.data?.labels;
    if (!labels || !labels[index]) return null;
    const keys = new Set(labels[index].keys || []);
    on ? keys.add(key) : keys.delete(key);
    return { ...el, data: { ...el.data, labels: labels.map((l, i) => (i === index ? { ...l, keys: [...keys] } : l)) } };
  });
}

// Custom labels only: a species label goes with its plant (removeSpecies) —
// short of that, its keys can only be switched off.
export function removeLabel(state, { elementId, index }) {
  return patchElement(state, elementId, (el) => {
    const labels = el.data?.labels;
    if (!labels || !labels[index] || labels[index].type === 'species') return null;
    return { ...el, data: { ...el.data, labels: labels.filter((_, i) => i !== index) } };
  });
}

// Drag-reorder: move label `from` to position `to`.
export function moveLabel(state, { elementId, from, to }) {
  return patchElement(state, elementId, (el) => {
    const labels = [...(el.data?.labels || [])];
    if (from === to || !labels[from] || to < 0 || to >= labels.length) return null;
    labels.splice(to, 0, labels.splice(from, 1)[0]);
    return { ...el, data: { ...el.data, labels } };
  });
}

// Replace a species label's shown keys (and their order) wholesale — the
// reorderable options list.
export function setLabelKeys(state, { elementId, index, keys }) {
  return patchElement(state, elementId, (el) => {
    const labels = el.data?.labels;
    const label = labels?.[index];
    if (!label || label.type !== 'species') return null;
    if ((label.keys || []).join() === keys.join()) return null;
    return { ...el, data: { ...el.data, labels: labels.map((l, i) => (i === index ? { ...l, keys: [...keys] } : l)) } };
  });
}

export function addLabel(state, { elementId, label }) {
  return patchElement(state, elementId, (el) => (
    { ...el, data: { ...el.data, labels: [...(el.data.labels || []), label] } }
  ));
}

// The element with `plant` attached: id into speciesIds, a species label with
// the v1 key set (a full set for the first plant, one line for later ones —
// see speciesLabelKeys), and — when it has no layer yet — the plant's layer
// (v1's `layer ??=`). Null when it already carries the plant.
function withSpecies(state, el, plant) {
  if ((el.speciesIds || []).includes(plant.id)) return null;
  const keys = speciesLabelKeys(plant, el.data?.labels);
  return {
    ...el,
    speciesIds: [...(el.speciesIds || []), plant.id],
    data: {
      ...el.data,
      labels: [...(el.data.labels || []), { type: 'species', id: plant.id, keys }],
      layer: el.data?.layer ?? speciesLayer(plant, state.layers || []),
    },
  };
}

// Attach a species to an element, and the plant object into the species cache.
export function addSpecies(state, { elementId, plant }) {
  const patch = patchElement(state, elementId, (el) => withSpecies(state, el, plant));
  if (!patch) return null;
  patch.species = { ...state.species, [plant.id]: plant };
  return patch;
}

// Attach a species to several elements at once (search-row "add to selection")
// — one undo step. Elements that already carry it are left alone.
export function addSpeciesToElements(state, { ids, plant }) {
  const set = new Set(ids);
  if (!set.size) return null;
  let hit = false;
  const elements = state.elements.map((el) => {
    const next = set.has(el.id) ? withSpecies(state, el, plant) : null;
    if (!next) return el;
    hit = true;
    return next;
  });
  if (!hit) return null;
  return { elements, species: { ...state.species, [plant.id]: plant } };
}

export function removeSpecies(state, { elementId, speciesId }) {
  return patchElement(state, elementId, (el) => {
    if (!(el.speciesIds || []).includes(speciesId)) return null;
    return {
      ...el,
      speciesIds: el.speciesIds.filter((x) => x !== speciesId),
      data: { ...el.data, labels: (el.data.labels || []).filter((l) => !(l.type === 'species' && l.id === speciesId)) },
    };
  });
}
